import { NoSsr } from '@mui/material';
import {
  CategoryTypes,
  type HeadlineResponse,
  type LatestForumPostsResponse,
  type MenuResponse,
  type NilHundredResponseNil100FilterResponseRelatedPagedData,
  type PlayerItemResponsePagedData,
  type ScoreboardResponseRelatedScheduleResponseRelatedPagedData,
  type TrendingListItemResponseTrendingListResponseRelatedPagedData,
} from '@on3/api';
import { useSite } from '@on3/ui-lib/src/contexts/SiteProvider';
import { MobileMPU } from 'components/Ads/MobileMPU';
import { MPU } from 'components/Ads/MPU';
import { PostFeed } from 'components/Forums/Forums';
import { CoverList } from 'components/Headlines/ArticleCover/CoverList';
import { FeedList } from 'components/Headlines/ArticleFeed/FeedList';
import { TopHeadlinesList } from 'components/Headlines/TopHeadlines/TopHeadlinesList';
import { SEOHomeText } from 'components/Home/SEOHomeText';
import { HomeLayout } from 'components/Layouts/HomeLayout';
import { SignUpFooter } from 'components/Newsletter/SignUpFooter';
import { Nil100Widget } from 'components/Nil/Nil100Widget';
import { PianoPromo } from 'components/Promos/PianoPromo';
import { ScheduleModule } from 'components/Schedules/Sidebar/Module';
import { TeamConnect } from 'components/Social/TeamConnect';
import { TributeGary } from 'components/Social/TributeGary';
import { LatestCommits } from 'components/Teams/LatestCommits';
import { QuickLinks } from 'components/Teams/QuickLinks';
import { TopHeadlines } from 'components/TopHeadlines';
import { useMemo } from 'react';
import { getArticleLists } from 'utils/articles';
import { createTitle } from 'utils/meta';
import { hasSiteSportCategory } from 'utils/sites';

import styles from './TraditionalHome.module.scss';

export interface ITraditionalHomeProps {
  featuredArticles: HeadlineResponse[] | null;
  siteSportsArticles: HeadlineResponse[] | null;
  siteRecruitingArticles: HeadlineResponse[] | null;
  siteHighSchoolArticles: HeadlineResponse[] | null;
  siteForums: LatestForumPostsResponse | null;
  channelSportsArticles: HeadlineResponse[] | null;
  channelRecruitingArticles: HeadlineResponse[] | null;
  highSchoolArticles: HeadlineResponse[] | null;
  commitsList: PlayerItemResponsePagedData | null;
  quickLinks: MenuResponse[] | null;
  nil100List: NilHundredResponseNil100FilterResponseRelatedPagedData | null;
  schedule: ScoreboardResponseRelatedScheduleResponseRelatedPagedData | null;
  trendingList:
    | TrendingListItemResponseTrendingListResponseRelatedPagedData[]
    | TrendingListItemResponseTrendingListResponseRelatedPagedData
    | null;
}

export const TraditionalHome = ({
  featuredArticles,
  siteSportsArticles,
  siteRecruitingArticles,
  siteHighSchoolArticles,
  siteForums,
  channelSportsArticles,
  channelRecruitingArticles,
  commitsList,
  quickLinks,
  nil100List,
  schedule,
  trendingList,
}: ITraditionalHomeProps) => {
  const { currentSite, siblingSite } = useSite();
  const { siteName, key: siteKey, url: siteUrl } = currentSite || {};
  const siteColor = currentSite?.primaryColor || '#fe3b1f';
  const siteCategories = useMemo(
    () => currentSite?.siteCategories || [],
    [currentSite],
  );

  const topHeadlinesHeading = createTitle(
    'Top',
    currentSite?.isChannel
      ? currentSite.siteName
      : currentSite?.organization?.knownAs || currentSite?.organization?.name,
    'Headlines',
  );

  const [
    featuredArticlesList,
    siteSportsArticlesList,
    siteRecruitingArticlesList,
    siteHighSchoolArticlesList,
    channelSportsArticlesList,
    channelRecruitingArticlesList,
  ] = getArticleLists({
    featuredArticles: {
      list: featuredArticles ?? [],
      sliceLimit: 8,
    },
    lists: [
      {
        list: siteSportsArticles ?? [],
        sliceLimit: 5,
        removeDuplicates: true,
      },
      {
        list: siteRecruitingArticles ?? [],
        sliceLimit: 5,
        removeDuplicates: true,
      },
      {
        list: siteHighSchoolArticles ?? [],
        sliceLimit: 5,
        removeDuplicates: true,
      },
      {
        list: channelSportsArticles ?? [],
        sliceLimit: 5,
        removeDuplicates: true,
      },
      {
        list: channelRecruitingArticles ?? [],
        sliceLimit: 5,
        removeDuplicates: true,
      },
    ],
  });

  const topHeadlinesLinks = [
    {
      title: 'View All',
      href: `${siblingSite?.url}/news/`,
    },
    {
      title: createTitle(
        siblingSite?.organization?.knownAs || siblingSite?.organization?.name,
        'Channel',
      ),
      href: `${siblingSite?.url}/`,
    },
  ];

  const recruitingViewAll = useMemo(
    () =>
      [
        { sport: 'Football', slug: 'football-recruiting' },
        { sport: 'Basketball', slug: 'basketball-recruiting' },
      ]
        .filter(({ sport }) =>
          hasSiteSportCategory(siteCategories, CategoryTypes.Recruiting, sport),
        )
        .map(({ sport, slug }) => ({
          url: `${siteUrl}/category/${slug}/news/`,
          text: `View All ${sport} Recruiting`,
          title: `${sport} Recruiting News`,
        })),
    [siteUrl, siteCategories],
  );

  let newsViewAll = useMemo(
    () =>
      [
        { sport: 'Football', slug: 'football' },
        { sport: 'Basketball', slug: 'basketball' },
      ]
        .filter(({ sport }) =>
          hasSiteSportCategory(siteCategories, CategoryTypes.Sport, sport),
        )
        .map(({ sport, slug }) => ({
          url: `${siteUrl}/category/${slug}/news/`,
          text: `View All ${sport}`,
          title: `${sport} News`,
        })),
    [siteUrl, siteCategories],
  );

  // KSR
  if (currentSite?.key === 395) {
    newsViewAll = newsViewAll.concat(recruitingViewAll);
  }

  const highSchoolViewAll = useMemo(
    () =>
      [
        { sport: 'Football', slug: 'high-school-football' },
        { sport: 'Basketball', slug: 'high-school-basketball' },
      ]
        .filter(({ sport }) =>
          hasSiteSportCategory(siteCategories, CategoryTypes.HighSchool, sport),
        )
        .map(({ sport, slug }) => ({
          url: `${siteUrl}/category/${slug}/news/`,
          text: `View All ${sport} High School`,
          title: `${sport} High School News`,
        })),
    [siteUrl, siteCategories],
  );

  const forumViewAll = siteForums?.primaryForum?.url
    ? [
        {
          url: siteForums.primaryForum.url,
          text: 'View All',
          title: 'View All Message Board Posts',
        },
      ]
    : [];

  const topHeadlinesList = siteSportsArticlesList?.length
    ? siteSportsArticlesList
    : [
        ...(channelSportsArticlesList || []),
        ...(channelRecruitingArticlesList || []),
      ];

  return (
    <>
      {/* @TEMP - until we have a creator page template */}
      {currentSite?.key === 488 && (
        <section className={styles.banner}>
          <div className={styles.bannerInner}>
            <img
              alt="Josh Pate Header"
              src="https://on3static.com/static/josh-pate/josh-pate-header.png"
            />
          </div>
        </section>
      )}
      {!!featuredArticlesList && (
        <CoverList
          authorColor="var(--header-text-color, #fff)"
          backgroundColor={siteColor}
          categoryColor="var(--header-text-color, #fff)"
          list={featuredArticlesList.slice(0, 3)}
          titleColor="var(--header-text-color, #fff)"
        >
          <NoSsr>
            <PianoPromo id="pianoPromoDark" />
          </NoSsr>
        </CoverList>
      )}
      <SEOHomeText />
      <HomeLayout>
        <section className={styles.feed}>
          <div className={styles.inner}>
            {!!featuredArticlesList.length && (
              <div data-section="news">
                <FeedList
                  list={featuredArticlesList.slice(3, 8)}
                  section={createTitle(siteName, 'Headlines')}
                  viewAllLinks={newsViewAll}
                />
              </div>
            )}

            {!!siteRecruitingArticlesList?.length && (
              <div data-section="recruiting">
                <FeedList
                  list={siteRecruitingArticlesList}
                  section={createTitle(siteName, 'Recruiting')}
                  viewAllLinks={recruitingViewAll}
                />
              </div>
            )}

            <MobileMPU id={'mobile_mpu_top'} incontent />

            {!!siteHighSchoolArticlesList?.length && (
              <div data-section="high-school">
                <FeedList
                  list={siteHighSchoolArticlesList}
                  section={createTitle(siteName, 'High School Sports')}
                  viewAllLinks={highSchoolViewAll}
                />
              </div>
            )}

            {!!siteForums?.posts?.length && (
              <div data-section="boards">
                <PostFeed
                  forum={siteForums?.primaryForum}
                  links={forumViewAll}
                  posts={siteForums?.posts}
                  title="Popular Message Board Posts"
                />
              </div>
            )}
          </div>
        </section>
        <aside className={styles.sidebarleft}>
          <div className={styles.inner}>
            {!!trendingList && <TopHeadlines trendingList={trendingList} />}

            {commitsList?.list?.length && (
              <LatestCommits list={commitsList?.list} />
            )}

            {!!schedule?.list?.length && (
              <ScheduleModule
                list={schedule?.list}
                relatedModel={schedule?.relatedModel}
              />
            )}

            <TeamConnect />
          </div>
        </aside>
        <aside className={styles.sidebarright}>
          <div className={styles.inner}>
            <MPU id={'mpu_top'} />

            {!!nil100List?.list?.length && (
              <Nil100Widget isModule list={nil100List?.list} />
            )}

            {!!quickLinks?.length && (
              <QuickLinks quickLinks={quickLinks} title="Quick Links" />
            )}

            {siteKey === 79 && <TributeGary />}

            {!!topHeadlinesList.length && (
              <div className={styles.topHeadlines}>
                <TopHeadlinesList
                  heading={topHeadlinesHeading}
                  isModule
                  links={topHeadlinesLinks}
                  list={topHeadlinesList.slice(0, 5)}
                  variant="secondary"
                />
              </div>
            )}

            <MPU id={'mpu_middle'} />
            <MobileMPU id={'mobile_mpu_middle'} incontent />
          </div>
        </aside>
      </HomeLayout>
      <SignUpFooter titleText={`${siteName} Newsletter`} />
    </>
  );
};
